import React from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SyncJobStatus, SyncJobType, SyncJobsFilters } from "@/types/pms";
import {
  DEFAULT_SYNC_JOBS_FILTERS,
  dateInputToIso,
  isoDateOnly,
} from "../utils/syncJobsFilters";

const STATUS_ALL = "ALL";
const TYPE_ALL = "ALL";

const statusOptionKeys: Record<SyncJobStatus, string> = {
  [SyncJobStatus.PENDING]: "pms.sync.filters.statusOptions.PENDING",
  [SyncJobStatus.RUNNING]: "pms.sync.filters.statusOptions.RUNNING",
  [SyncJobStatus.COMPLETED]: "pms.sync.filters.statusOptions.COMPLETED",
  [SyncJobStatus.FAILED]: "pms.sync.filters.statusOptions.FAILED",
  [SyncJobStatus.CANCELLED]: "pms.sync.filters.statusOptions.CANCELLED",
};

const typeOptionKeys: Record<SyncJobType, string> = {
  [SyncJobType.FULL]: "pms.sync.filters.typeOptions.FULL",
  [SyncJobType.INCREMENTAL]: "pms.sync.filters.typeOptions.INCREMENTAL",
  [SyncJobType.SELECTIVE]: "pms.sync.filters.typeOptions.SELECTIVE",
};

interface SyncJobsFiltersBarProps {
  filters: SyncJobsFilters;
  onChange: (next: SyncJobsFilters) => void;
}

export const SyncJobsFiltersBar: React.FC<SyncJobsFiltersBarProps> = ({
  filters,
  onChange,
}) => {
  const { t } = useTranslation();

  const handleStatusChange = (value: string) => {
    if (value === STATUS_ALL) {
      onChange({ ...filters, status: undefined });
      return;
    }
    onChange({ ...filters, status: value as SyncJobStatus });
  };

  const handleTypeChange = (value: string) => {
    if (value === TYPE_ALL) {
      onChange({ ...filters, type: undefined });
      return;
    }
    onChange({ ...filters, type: value as SyncJobType });
  };

  const handleFromChange = (value: string) => {
    onChange({ ...filters, from: dateInputToIso(value, false) });
  };

  const handleToChange = (value: string) => {
    onChange({ ...filters, to: dateInputToIso(value, true) });
  };

  const handleClear = () => {
    onChange({ ...DEFAULT_SYNC_JOBS_FILTERS });
  };

  const hasActiveFilters = Boolean(
    filters.from || filters.to || filters.status || filters.type,
  );

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:flex lg:flex-wrap lg:items-end">
      <div className="flex flex-col gap-1.5">
        <Label
          htmlFor="sync-jobs-from"
          className="text-xs text-muted-foreground"
        >
          {t("pms.sync.filters.from")}
        </Label>
        <Input
          id="sync-jobs-from"
          type="date"
          value={isoDateOnly(filters.from)}
          onChange={(e) => handleFromChange(e.target.value)}
          className="h-10 w-full lg:w-[160px]"
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="sync-jobs-to" className="text-xs text-muted-foreground">
          {t("pms.sync.filters.to")}
        </Label>
        <Input
          id="sync-jobs-to"
          type="date"
          value={isoDateOnly(filters.to)}
          onChange={(e) => handleToChange(e.target.value)}
          className="h-10 w-full lg:w-[160px]"
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label
          htmlFor="sync-jobs-status"
          className="text-xs text-muted-foreground"
        >
          {t("pms.sync.filters.status")}
        </Label>
        <Select
          value={filters.status ?? STATUS_ALL}
          onValueChange={handleStatusChange}
        >
          <SelectTrigger
            id="sync-jobs-status"
            size="sm"
            className="h-10 w-full lg:w-[180px]"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={STATUS_ALL}>
              {t("pms.sync.filters.statusAll")}
            </SelectItem>
            {Object.values(SyncJobStatus).map((status) => (
              <SelectItem key={status} value={status}>
                {t(statusOptionKeys[status])}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label
          htmlFor="sync-jobs-type"
          className="text-xs text-muted-foreground"
        >
          {t("pms.sync.filters.type")}
        </Label>
        <Select
          value={filters.type ?? TYPE_ALL}
          onValueChange={handleTypeChange}
        >
          <SelectTrigger
            id="sync-jobs-type"
            size="sm"
            className="h-10 w-full lg:w-[160px]"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={TYPE_ALL}>
              {t("pms.sync.filters.typeAll")}
            </SelectItem>
            {Object.values(SyncJobType).map((type) => (
              <SelectItem key={type} value={type}>
                {t(typeOptionKeys[type])}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {hasActiveFilters && (
        <Button
          type="button"
          variant="new_ghost"
          size="sm"
          onClick={handleClear}
          className="h-10 sm:col-span-2 sm:justify-self-start lg:col-auto"
        >
          <X className="size-4" />
          {t("pms.sync.filters.clear")}
        </Button>
      )}
    </div>
  );
};
